import type {
  ContactValue,
  CoordinateValue,
  JsonSchemaType,
  PositionsValue,
  StatusOption,
  StatusValue,
} from '@gmn-portal/backend';
import { DateTime } from 'luxon';
import { useContract, useCustomer, useObjects } from '@/core/api';
import { useGlobalMetadata } from '@/core/api/global-metadata';
import { useIntl } from '@/core/i18n/use-intl';
import { useCoordinateFormat } from '@/shared/hooks/use-coordinate-format';
import { formatCoordinateForCopy } from '@/shared/utils';
import { formatCurrencyFromCents } from '@/shared/utils/money';

import type { ResolvedFieldValueProps } from './types';

const isCoordinateValue = (value: unknown): value is CoordinateValue => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as { latitude?: unknown; longitude?: unknown };
  return candidate.latitude !== undefined && candidate.longitude !== undefined;
};

type StatusValueWithOptionIds = StatusValue & {
  manualStatusOptionId?: string;
  defaultStatusOptionId?: string;
};

const ensureStatusOptionsWithIds = (statusOptions?: StatusOption[]) => {
  if (!statusOptions) return undefined;

  return statusOptions.map((option, index) => ({
    ...option,
    id: option.id?.trim() || `status-opt-${option.color}-${index + 1}`,
  }));
};

function formatContactValue(contact: ContactValue | undefined | null): string {
  if (!contact) return '';

  const parts = [contact.name, contact.email, contact.phone1, contact.phone2].filter(Boolean);
  if (contact.fallbackContact?.name) {
    parts.push(`[Fallback: ${contact.fallbackContact.name}]`);
  }

  return parts.join(', ');
}

function formatPositionsValue(
  positions: PositionsValue | undefined | null,
  positionsLabel: string
): string {
  if (!positions || !Array.isArray(positions) || positions.length === 0) return '';

  const labels = positions
    .map((position) => position.label)
    .filter(Boolean)
    .slice(0, 3);

  if (labels.length === 0) {
    return `${positions.length} ${positionsLabel}`;
  }

  const preview = labels.join(', ');
  return positions.length > 3 ? `${preview}, ... (${positions.length})` : preview;
}

function getStatusLabel(
  color: string | undefined,
  statusOptions: StatusOption[] | undefined,
  locale: string,
  optionId?: string
): string {
  if (!color) return '';
  const normalizedStatusOptions = ensureStatusOptionsWithIds(statusOptions);
  if (!normalizedStatusOptions) return color;

  const option =
    normalizedStatusOptions.find((opt) => opt.id === optionId) ||
    normalizedStatusOptions.find((opt) => opt.color === color);
  if (!option) return color;

  return (locale === 'en-US' ? option.labelEn : option.labelDe) || option.labelDe;
}

function formatStatusValue(
  status: StatusValueWithOptionIds | undefined | null,
  statusOptions: StatusOption[] | undefined,
  locale: string,
  translations: { manual: string; scheduled: string; ranges: string; defaultStatus: string }
): string {
  if (!status || typeof status !== 'object') return '';

  const parts: string[] = [];
  if (status.mode === 'manual') {
    const statusLabel = getStatusLabel(
      status.manualStatus,
      statusOptions,
      locale,
      status.manualStatusOptionId
    );
    parts.push(`${translations.manual}: ${statusLabel}`);
  } else if (status.mode === 'scheduled') {
    const rangeCount = status.ranges?.length || 0;
    if (rangeCount > 0) {
      parts.push(`${translations.scheduled}: ${rangeCount} ${translations.ranges}`);
    }
    const defaultLabel = getStatusLabel(
      status.defaultStatus,
      statusOptions,
      locale,
      status.defaultStatusOptionId
    );
    parts.push(`${translations.defaultStatus}: ${defaultLabel}`);
  }

  return parts.join(', ');
}

export function ResolvedFieldValue({
  field,
  value,
  contract,
  metadataSchemaOverride,
  dynamicSchemaOverride,
}: ResolvedFieldValueProps) {
  const { t, locale } = useIntl();
  const { data: globalMetadata } = useGlobalMetadata();
  const coordinateFormat = useCoordinateFormat();

  const parentContractId = field === 'parentContract' && typeof value === 'string' ? value : '';
  const { data: parentContract } = useContract(parentContractId, {
    disabled: !parentContractId,
  });

  if (value === null || value === undefined || value === '') {
    return <span>{t('activity.none')}</span>;
  }

  if (field === 'dynamicMetadataSchema') {
    if (typeof value !== 'object' || !value) {
      return <span>{t('activity.none')}</span>;
    }
    const schema = value as JsonSchemaType;
    const count = Object.keys(schema.properties || {}).length;
    return <span>{t('activity.schemaSummary', { count })}</span>;
  }

  // Handle status field specifically
  if (field === 'status') {
    if (value === 'open' || value === 'closed') {
      return <span>{t(`contracts.status.${value}`)}</span>;
    }
  }

  if (field === 'billingInterval' && typeof value === 'string') {
    return <span>{t(`contracts.billingIntervalOptions.${value}` as any)}</span>;
  }

  if (field === 'billingGenerationMode' && typeof value === 'string') {
    return <span>{t(`contracts.billingGenerationMode.${value}` as any)}</span>;
  }

  if (field === 'billingTotalCents' && typeof value === 'number') {
    return <span>{formatCurrencyFromCents(value, locale)}</span>;
  }

  if (field === 'paymentTermsDays' && typeof value === 'number') {
    return <span>{value}</span>;
  }

  if ((field === 'startDate' || field === 'endDate') && typeof value === 'string') {
    const date = DateTime.fromISO(value);
    if (date.isValid) {
      return <span>{date.toFormat('dd.MM.yyyy')}</span>;
    }
  }

  // Handle metadata fields that may be nested objects/relationships
  if (field.startsWith('metadata.')) {
    const metadataKey = field.split('.')[1];

    const metadataSchema =
      metadataSchemaOverride ??
      (globalMetadata?.rootContractMetadata as JsonSchemaType | undefined);

    // Check if this field has a specific type in the schema
    const fieldSchema = metadataSchema?.properties?.[metadataKey];

    if (fieldSchema) {
      const rendered = renderSchemaValue(fieldSchema, value, {
        locale,
        coordinateFormat,
        none: t('activity.none'),
        yes: t('common.yes'),
        no: t('common.no'),
        positionsLabel: t('activity.object.positions' as never) as string,
        translations: {
          manual: t('activity.object.status.manual' as never) as string,
          scheduled: t('activity.object.status.scheduled' as never) as string,
          ranges: t('activity.object.status.ranges' as never) as string,
          defaultStatus: t('activity.object.status.default' as never) as string,
        },
      });
      if (rendered !== null) return <span>{rendered}</span>;
    }
  }

  // Handle dynamic metadata fields
  if (field.startsWith('dynamicMetadata.')) {
    const metadataKey = field.split('.')[1];
    const fieldSchema =
      dynamicSchemaOverride?.properties?.[metadataKey] ??
      contract?.dynamicMetadataSchema?.properties?.[metadataKey];

    if (fieldSchema) {
      const rendered = renderSchemaValue(fieldSchema, value, {
        locale,
        coordinateFormat,
        none: t('activity.none'),
        yes: t('common.yes'),
        no: t('common.no'),
        positionsLabel: t('activity.object.positions' as never) as string,
        translations: {
          manual: t('activity.object.status.manual' as never) as string,
          scheduled: t('activity.object.status.scheduled' as never) as string,
          ranges: t('activity.object.status.ranges' as never) as string,
          defaultStatus: t('activity.object.status.default' as never) as string,
        },
      });
      if (rendered !== null) return <span>{rendered}</span>;
    }
  }

  // Handle objects field
  if (field === 'objects' && Array.isArray(value)) {
    return <ObjectsDisplay objectIds={value} />;
  }

  if (field === 'aggregatedObjects' && Array.isArray(value)) {
    return <ObjectsDisplay objectIds={value} />;
  }

  // Handle customer field
  if (field === 'customer' && typeof value === 'string') {
    return <CustomerDisplay customerId={value} />;
  }

  if (field === 'parentContract' && typeof value === 'string') {
    return <span>{parentContract?.title || parentContract?.orderNumber || value}</span>;
  }

  if (field.startsWith('billingPeriods.')) {
    if (field.endsWith('.status') && typeof value === 'string') {
      return <span>{t(`contracts.billingStatus.${value}` as any)}</span>;
    }

    if (typeof value === 'string') {
      const date = DateTime.fromISO(value);
      if (date.isValid) {
        return <span>{date.toFormat('dd.MM.yyyy')}</span>;
      }
      return <span>{String(value)}</span>;
    }

    if (typeof value === 'number') {
      if (field.endsWith('amountCents')) {
        return <span>{formatCurrencyFromCents(value, locale)}</span>;
      }
      return <span>{value}</span>;
    }

    if (typeof value === 'object' && value) {
      const v = value as Record<string, unknown>;
      if (typeof v.startDate === 'string' && typeof v.endDate === 'string') {
        const start = DateTime.fromISO(v.startDate);
        const end = DateTime.fromISO(v.endDate);
        if (start.isValid && end.isValid) {
          const parts = [`${start.toFormat('dd.MM.yyyy')} – ${end.toFormat('dd.MM.yyyy')}`];
          if (typeof v.status === 'string') {
            parts.push(t(`contracts.billingStatus.${v.status}` as any) as string);
          }
          if (typeof v.amountCents === 'number') {
            parts.push(formatCurrencyFromCents(v.amountCents, locale));
          }
          return <span>{parts.join(', ')}</span>;
        }
      }
      return <span>{t('activity.none')}</span>;
    }
  }

  if (field.endsWith('.status') && typeof value === 'string') {
    return <span>{t(`contracts.billingStatus.${value}` as any)}</span>;
  }

  // Handle other fields
  if (typeof value === 'string') {
    return <span>{value}</span>;
  }

  if (typeof value === 'number') {
    return <span>{value}</span>;
  }

  if (typeof value === 'boolean') {
    return <span>{value ? t('common.yes') : t('common.no')}</span>;
  }

  // Coordinate values (e.g. metadata coordinate fields) must not stringify to
  // "[object Object]".
  if (isCoordinateValue(value)) {
    const formatted = formatCoordinateForCopy(value, coordinateFormat);
    return formatted ? <span>{formatted}</span> : <span>{t('activity.none')}</span>;
  }

  // Never render a raw object as "[object Object]".
  if (typeof value === 'object') {
    return <span>{t('activity.none')}</span>;
  }

  // Fallback for other types
  return <span>{String(value)}</span>;
}

const ObjectsDisplay = ({ objectIds }: { objectIds: string[] }) => {
  const { data: objectsData, isLoading } = useObjects({
    where: {
      id: {
        in: objectIds,
      },
    },
    limit: objectIds.length,
    disabled: !objectIds || objectIds.length === 0,
  });

  if (isLoading) return <span>Loading...</span>;

  const objects = objectsData?.docs || [];
  if (objects.length === 0) return <span>-</span>;

  return <span>{objects.map((obj) => obj.name).join(', ')}</span>;
};

const CustomerDisplay = ({ customerId }: { customerId: string }) => {
  const { data: customer, isLoading } = useCustomer(customerId);

  if (isLoading) return <span>Loading...</span>;
  return <span>{customer?.name || '-'}</span>;
};

function renderSchemaValue(
  fieldSchema: JsonSchemaType['properties'][string],
  value: unknown,
  options: {
    locale: string;
    coordinateFormat: ReturnType<typeof useCoordinateFormat>;
    none: string;
    yes: string;
    no: string;
    positionsLabel: string;
    translations: { manual: string; scheduled: string; ranges: string; defaultStatus: string };
  }
): string | null {
  switch (fieldSchema.type) {
    case 'date': {
      if (typeof value !== 'string') return String(value);
      const date = DateTime.fromISO(value);
      return date.isValid ? date.toFormat('dd.MM.yyyy') : String(value);
    }
    case 'boolean':
      return typeof value === 'boolean' ? (value ? options.yes : options.no) : String(value);
    case 'number':
      return typeof value === 'number' ? value.toString() : String(value);
    case 'contact': {
      const formatted = formatContactValue(value as ContactValue | undefined | null);
      return formatted || options.none;
    }
    case 'positions': {
      const formatted = formatPositionsValue(
        value as PositionsValue | undefined | null,
        options.positionsLabel
      );
      return formatted || options.none;
    }
    case 'status': {
      const formatted = formatStatusValue(
        value as StatusValueWithOptionIds | undefined | null,
        (fieldSchema as { statusOptions?: StatusOption[] }).statusOptions,
        options.locale,
        options.translations
      );
      return formatted || options.none;
    }
    case 'coordinate': {
      const formatted = isCoordinateValue(value)
        ? formatCoordinateForCopy(value, options.coordinateFormat)
        : '';
      return formatted || options.none;
    }
    default:
      return null;
  }
}
